import {
  getLuminance,
  mix,
  parseToHsl,
  parseToRgb,
  rgba,
  saturate,
  toColorString,
} from 'polished';
import {
  AQBLOCKS_BORDER_SIZE,
  AQBLOCKS_BORDER_SIZECLAMP,
  AQBLOCKS_CORNER,
  AQBLOCKS_CORNERCLAMP,
  AQBLOCKS_FONT_FAMILY,
  AQBLOCKS_LABEL_FONTSIZE,
  AQBLOCKS_LEAFRD_SIZE,
  AQBLOCKS_REFERENCE_CORNER,
  AQBLOCKS_REFERENCE_CORNERCLAMP,
  AQBLOCKS_REFERENCE_MARKER_SIZE,
  AQBLOCKS_REFERENCE_MARKER_SIZECLAMP,
  AQBLOCKS_REFERENCE_SIZE,
  AQBLOCKS_REFERENCE_SIZECLAMP,
  AQBLOCKS_SELECTBORDER_SIZE,
  AQBLOCKS_SELECTBORDER_SIZECLAMP,
  AQBLOCKS_SELECTEDREFERENCE_SIZE,
  AQBLOCKS_SELECTEDREFERENCE_SIZECLAMP,
  AQBLOCKS_SUBLABEL_BORDERCLAMP,
  AQBLOCKS_SUBLABEL_CORNER,
  AQBLOCKS_SUBLABEL_CORNERCLAMP,
  AQBLOCKS_SUBLABEL_FONTSIZE,
  AQBLOCKS_TINT,
} from '../consts';
import { clamp } from 'lodash';
import { interp } from 'tabview/visualizations/misc/math';
import { RenderContext, RenderParameters } from './types';
import { LineType } from '@ardoq/api-types';
import { BlocksNode } from '../types';

const lightenBy = (scale: number, color: string) => {
  if (color === 'transparent') {
    return color;
  }

  const hslColor = parseToHsl(color);
  const lightness = interp(scale, hslColor.lightness, 1);

  return toColorString({
    ...hslColor,
    lightness: lightness,
  });
};

export const darkenBy = (scale: number, color: string) => {
  if (color === 'transparent') {
    return color;
  }

  const hslColor = parseToHsl(color);
  const lightness = interp(scale, hslColor.lightness, 0);

  return toColorString({
    ...hslColor,
    lightness: lightness,
  });
};

const luminancePivot = 0.375;

export const createRenderContext = (
  params: RenderParameters
): RenderContext => {
  /* TODO I'm pretty sure I can get _pixel straight from the projection */
  const v0 = params.projection.unprojectPoint([1, 0]);
  const v1 = params.projection.unprojectPoint([0, 0]);
  const _pixel = Math.max(Math.abs(v0[0] - v1[0]), Math.abs(v0[1] - v1[1]));

  const tint = AQBLOCKS_TINT;
  const labelFontSize =
    Math.round((10 * AQBLOCKS_LABEL_FONTSIZE) / _pixel) / 10;
  const labelAlpha = clamp((labelFontSize - 1) / 5, 0, 1);
  const sublabelFontSize =
    Math.round((10 * AQBLOCKS_SUBLABEL_FONTSIZE) / _pixel) / 10;
  const sublabelAlpha = clamp((sublabelFontSize - 1) / 5, 0, 1);

  const referenceSize = clamp(
    AQBLOCKS_REFERENCE_SIZE / _pixel,
    AQBLOCKS_REFERENCE_SIZECLAMP[0],
    AQBLOCKS_REFERENCE_SIZECLAMP[1]
  );

  const closedRdSize = AQBLOCKS_LEAFRD_SIZE / _pixel;
  const closedRdAlpha = clamp((closedRdSize - 3) / 5, 0, 1);

  // #region contrast
  const contrast = (color: string): string => {
    let ret = _contrast.get(color);

    if (!ret) {
      const readable =
        getLuminance(color) < luminancePivot ? '#ffffff' : '#000000';
      ret = blend(tint, color, readable);

      _contrast.set(color, ret);
    }

    return ret;
  };
  const _contrast = new Map<string, string>();
  // #endregion

  // #region blend
  const blend = (p: number, min: string, max: string) => {
    const key = `${p}/${min}/${max}`;
    let ret = _blend.get(key);

    if (!ret) {
      ret = mix(p, max, min);
      _blend.set(key, ret);
    }

    return ret;
  };
  const _blend = new Map<string, string>();
  // #endregion

  // #region border
  const border = (color: string) => {
    let ret = _border.get(color);

    if (!ret) {
      ret =
        getLuminance(color) < luminancePivot
          ? lightenBy(0.1, color)
          : darkenBy(0.1, color);
      _border.set(color, ret);
    }

    return ret;
  };
  const _border = new Map<string, string>();
  // #endregion

  // #region nodeFill
  const nodeFill = (node: BlocksNode) => {
    //return isGroup(node)? "#f8f8f8": "#ffffff";

    const color = node.color;

    let ret = _nodeFill.get(color);

    if (!ret) {
      ret = lightenBy(0.9, color);
      _nodeFill.set(color, ret);
    }

    return ret;
  };
  const _nodeFill = new Map<string, string>();
  // #endregion

  // #region nodeHoverFill
  const nodeHoverFill = (node: BlocksNode) => {
    //return isGroup(node)? "#f4f4f4": "#f8f8f8";

    const color = node.color;

    let ret = _nodeHoverFill.get(color);

    if (!ret) {
      ret = saturate(0.15, lightenBy(0.85, color));
      _nodeHoverFill.set(color, ret);
    }

    return ret;
  };
  const _nodeHoverFill = new Map<string, string>();
  // #endregion

  // #region nodeBorder
  const nodeBorder = (node: BlocksNode) => {
    //return '#000000';

    const color = node.color;

    return color;
  };
  // #endregion

  // #region referenceHoverStroke
  const referenceHoverStroke = (color: string) => {
    return color;
  };
  // #endregion

  // #region referenceMarkerFill
  const referenceMarkerFill = (color: string) => {
    let ret = _referenceMarkerFill.get(color);

    if (!ret) {
      ret =
        getLuminance(color) < luminancePivot
          ? lightenBy(0.5, color)
          : darkenBy(0.5, color);
      _referenceMarkerFill.set(color, ret);
    }

    return ret;
  };
  const _referenceMarkerFill = new Map<string, string>();
  // #endregion

  // #region referenceDashArray
  const dashArray = (type: LineType) => {
    const size = Math.max(referenceSize, 1);
    const key = type.toString();
    let ret = _dashArray.get(key);

    if (!ret) {
      switch (type) {
        case LineType.DOTTED:
          ret = [size, 2 * size];
          break;

        case LineType.DASHED:
          ret = [2 * size, 3 * size];
          break;

        default:
          ret = [];
      }

      _dashArray.set(key, ret);
    }

    return ret;
  };
  const _dashArray = new Map<string, number[]>();
  // #endregion

  // #region labelColor
  const labelColor = (color: string) => {
    let ret = _labelColor.get(color);

    if (!ret) {
      const readable = contrast(color);

      if (labelAlpha >= 1) {
        ret = readable;
      } else {
        const rgb = parseToRgb(readable);

        ret = rgba(rgb.red, rgb.green, rgb.blue, labelAlpha);
      }

      _labelColor.set(color, ret);
    }

    return ret;
  };
  const _labelColor = new Map<string, string>();
  // #endregion

  // #region sublabelFill
  const sublabelFill = (color: string | undefined, fill: string) => {
    const key = color ?? `undefined${fill}`;
    let value = _sublabelFill.get(key);

    if (!value) {
      value = color ? blend(0.3, fill, color) : darkenBy(0.02, fill);
      _sublabelColor.set(key, value);
    }

    return value;
  };
  const _sublabelFill = new Map<string, string>();
  // #endregion

  // #region sublabelColor
  const sublabelColor = (color: string) => {
    let ret = _sublabelColor.get(color);

    if (!ret) {
      const readable = contrast(color);

      if (sublabelAlpha >= 1) {
        ret = readable;
      } else {
        const rgb = parseToRgb(readable);

        ret = rgba(rgb.red, rgb.green, rgb.blue, sublabelAlpha);
      }

      _sublabelColor.set(color, ret);
    }

    return ret;
  };
  const _sublabelColor = new Map<string, string>();
  // #endregion

  return {
    ...params,
    contrast: contrast,
    blend: blend,
    pixel: () => _pixel,
    border: border,

    /* generic nodes */

    nodeFill: nodeFill,
    nodeHoverFill: nodeHoverFill,
    nodeBorder: nodeBorder,

    borderSize: Math.round(
      clamp(
        AQBLOCKS_BORDER_SIZE / _pixel,
        AQBLOCKS_BORDER_SIZECLAMP[0],
        AQBLOCKS_BORDER_SIZECLAMP[1]
      )
    ),
    selectBorderSize: Math.round(
      clamp(
        AQBLOCKS_SELECTBORDER_SIZE / _pixel,
        AQBLOCKS_SELECTBORDER_SIZECLAMP[0],
        AQBLOCKS_SELECTBORDER_SIZECLAMP[1]
      )
    ),
    cornerRadius: Math.round(
      clamp(
        AQBLOCKS_CORNER / _pixel,
        AQBLOCKS_CORNERCLAMP[0],
        AQBLOCKS_CORNERCLAMP[1]
      )
    ),

    /* references */

    referenceHoverStroke: referenceHoverStroke,
    referenceSize: referenceSize,
    selectedReferenceSize: clamp(
      AQBLOCKS_SELECTEDREFERENCE_SIZE / _pixel,
      AQBLOCKS_SELECTEDREFERENCE_SIZECLAMP[0],
      AQBLOCKS_SELECTEDREFERENCE_SIZECLAMP[1]
    ),
    referenceMarkerSize: clamp(
      AQBLOCKS_REFERENCE_MARKER_SIZE / _pixel,
      AQBLOCKS_REFERENCE_MARKER_SIZECLAMP[0],
      AQBLOCKS_REFERENCE_MARKER_SIZECLAMP[1]
    ),
    referenceMarkerFill: referenceMarkerFill,
    referenceCornerRadius: clamp(
      AQBLOCKS_REFERENCE_CORNER / _pixel,
      AQBLOCKS_REFERENCE_CORNERCLAMP[0],
      AQBLOCKS_REFERENCE_CORNERCLAMP[1]
    ),
    dashArray: dashArray,

    /* labels and sublabels */

    labelFont: `${labelFontSize}px ${AQBLOCKS_FONT_FAMILY}`,
    labelColor: labelColor,
    labelAlpha: labelAlpha,

    sublabelFont: `${sublabelFontSize}px ${AQBLOCKS_FONT_FAMILY}`,
    sublabelBorderThickness: clamp(
      1.0 / _pixel,
      AQBLOCKS_SUBLABEL_BORDERCLAMP[0],
      AQBLOCKS_SUBLABEL_BORDERCLAMP[1]
    ),
    sublabelCornerRadius: clamp(
      AQBLOCKS_SUBLABEL_CORNER / _pixel,
      AQBLOCKS_SUBLABEL_CORNERCLAMP[0],
      AQBLOCKS_SUBLABEL_CORNERCLAMP[1]
    ),
    sublabelColor: sublabelColor,
    sublabelFill: sublabelFill,
    sublabelAlpha: sublabelAlpha,

    /* representation data */

    closedRdAlpha: closedRdAlpha,
  };
};
